// Render live video pixels, rather than a decorative checkerboard.
export const MOSAIC_CELL_SIZE = 16;
const BLUR_RADIUS = 8;
const blurBuffers = new WeakMap();

// Blur the cell image. Edge copies go to a separate canvas so they never
// force repeated snapshots of a canvas being read and written simultaneously.
function blurCells(sample, blurred, columns, rows, padding, radius, clampEdges) {
  let input = sample;
  if (clampEdges) {
    let edgeCanvas = blurBuffers.get(sample);
    if (!edgeCanvas) {
      edgeCanvas = sample.ownerDocument.createElement('canvas');
      blurBuffers.set(sample, edgeCanvas);
    }
    if (edgeCanvas.width !== sample.width || edgeCanvas.height !== sample.height) {
      edgeCanvas.width = sample.width; edgeCanvas.height = sample.height;
    }
    const edgeCtx = edgeCanvas.getContext('2d');
    edgeCtx.clearRect(0, 0, edgeCanvas.width, edgeCanvas.height);
    edgeCtx.imageSmoothingEnabled = false;
    edgeCtx.drawImage(sample, padding, padding, columns, rows, padding, padding, columns, rows);
    // Include corners in the side strips, then fill top/bottom from the source
    // interior row. All copies read the unchanged sample texture.
    edgeCtx.drawImage(sample, padding, padding, 1, rows, 0, padding, padding, rows);
    edgeCtx.drawImage(sample, padding + columns - 1, padding, 1, rows, padding + columns, padding, padding, rows);
    edgeCtx.drawImage(sample, padding, padding, columns, 1, padding, 0, columns, padding);
    edgeCtx.drawImage(sample, padding, padding + rows - 1, columns, 1, padding, padding + rows, columns, padding);
    for (const x of [0, padding + columns]) for (const y of [0, padding + rows]) {
      edgeCtx.drawImage(sample, x ? padding + columns - 1 : padding, y ? padding + rows - 1 : padding,
        1, 1, x, y, padding, padding);
    }
    input = edgeCanvas;
  }
  if (blurred.width !== columns || blurred.height !== rows) { blurred.width = columns; blurred.height = rows; }
  const blurCtx = blurred.getContext('2d');
  blurCtx.clearRect(0, 0, columns, rows);
  blurCtx.filter = `blur(${radius}px)`;
  blurCtx.drawImage(input, -padding, -padding);
  blurCtx.filter = 'none';
}

export function intersect(a, b) {
  const left = Math.max(a.left, b.left);
  const top = Math.max(a.top, b.top);
  const right = Math.min(a.left + a.width, b.left + b.width);
  const bottom = Math.min(a.top + a.height, b.top + b.height);
  return { left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

export function getVideoSources(frame, overlayRect) {
  // A separate-origin Jitsi deployment cannot expose its video DOM. The
  // component enables a real backdrop blur when sampling is unavailable.
  let doc;
  try { doc = frame?.contentDocument; } catch { return []; }
  if (!doc) return [];
  const frameRect = frame.getBoundingClientRect();
  return Array.from(doc.querySelectorAll('video')).flatMap(video => {
    if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) return [];
    const view = doc.defaultView;
    const box = video.getBoundingClientRect();
    if (!box.width || !box.height) return [];
    let clip = intersect(box, { left: 0, top: 0, width: frame.clientWidth, height: frame.clientHeight });
    let mirrored = false;
    for (let node = video; node && node !== doc.documentElement; node = node.parentElement) {
      const style = view.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return [];
      if (style.transform !== 'none' && new view.DOMMatrix(style.transform).a < 0) mirrored = !mirrored;
      if (node !== video && /(hidden|clip|scroll|auto)/.test(style.overflowX + style.overflowY)) {
        clip = intersect(clip, node.getBoundingClientRect());
      }
    }
    const style = view.getComputedStyle(video);
    const scale = style.objectFit === 'contain'
      ? Math.min(box.width / video.videoWidth, box.height / video.videoHeight)
      : Math.max(box.width / video.videoWidth, box.height / video.videoHeight);
    const width = style.objectFit === 'fill' ? box.width : video.videoWidth * scale;
    const height = style.objectFit === 'fill' ? box.height : video.videoHeight * scale;
    const position = style.objectPosition.split(' ');
    const fraction = value => value?.endsWith('%') ? parseFloat(value) / 100 : 0.5;
    const offsetX = frameRect.left - overlayRect.left;
    const offsetY = frameRect.top - overlayRect.top;
    return [{
      video, mirrored,
      box: { left: box.left + offsetX + (box.width - width) * fraction(position[0]),
        top: box.top + offsetY + (box.height - height) * fraction(position[1]), width, height },
      clip: { ...clip, left: clip.left + offsetX, top: clip.top + offsetY }
    }];
  });
}

// Check coverage using geometry, without expensive canvas pixel readbacks.
export function coversRegion(rectangles, region) {
  if (!region.width || !region.height) return false;
  const right = region.left + region.width;
  const bottom = region.top + region.height;
  const clipped = rectangles.map(rect => intersect(rect, region)).filter(rect => rect.width && rect.height);
  const edges = [...new Set([region.left, right, ...clipped.flatMap(rect => [rect.left, rect.left + rect.width])])].sort((a, b) => a - b);
  for (let i = 1; i < edges.length; i++) {
    const x = (edges[i - 1] + edges[i]) / 2;
    const intervals = clipped.filter(rect => rect.left <= x && rect.left + rect.width >= x).sort((a, b) => a.top - b.top);
    let y = region.top;
    for (const rect of intervals) {
      if (rect.top > y + 0.001) return false;
      y = Math.max(y, rect.top + rect.height);
    }
    if (y < bottom - 0.001) return false;
  }
  return true;
}

export function updateMosaicFallback(canvas, covered) {
  const style = canvas.parentElement?.style;
  if (!style) return;
  const filter = covered ? 'none' : 'blur(28px)';
  if (style.backdropFilter !== filter) style.backdropFilter = filter;
  if (style.webkitBackdropFilter !== filter) style.webkitBackdropFilter = filter;
}

export function renderMosaic(canvas, sample, blurred, sources, region) {
  const width = Math.max(1, Math.ceil(region.width));
  const height = Math.max(1, Math.ceil(region.height));
  const columns = Math.max(1, Math.ceil(width / MOSAIC_CELL_SIZE));
  const rows = Math.max(1, Math.ceil(height / MOSAIC_CELL_SIZE));
  // On screen, CSS pixelated scaling can display the tiny cell image directly.
  // Full-resolution output remains available for pixel tests/export callers.
  const outputWidth = region.lowResolution ? columns : width;
  const outputHeight = region.lowResolution ? rows : height;
  if (canvas.width !== outputWidth || canvas.height !== outputHeight) { canvas.width = outputWidth; canvas.height = outputHeight; }
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, outputWidth, outputHeight);
  if (!sources.length || !region.width || !region.height) return false;

  // One low-resolution pixel becomes one displayed mosaic cell. Gaussian
  // blur runs on this small image, never on the full-resolution video.
  const scaleX = columns / region.width;
  const scaleY = rows / region.height;
  const radius = BLUR_RADIUS * Math.max(scaleX, scaleY);
  const padding = Math.ceil(radius * 3);
  const paddedWidth = columns + padding * 2;
  const paddedHeight = rows + padding * 2;
  const paddedRegion = { left: region.left - padding / scaleX, top: region.top - padding / scaleY,
    width: paddedWidth / scaleX, height: paddedHeight / scaleY };
  if (sample.width !== paddedWidth || sample.height !== paddedHeight) {
    sample.width = paddedWidth; sample.height = paddedHeight;
  }
  const sampleCtx = sample.getContext('2d');
  sampleCtx.clearRect(0, 0, paddedWidth, paddedHeight);
  sampleCtx.imageSmoothingEnabled = true;
  sampleCtx.imageSmoothingQuality = 'high';
  const coverage = [];
  for (const source of sources) {
    const { box, clip, video, mirrored } = source;
    const visible = intersect(intersect(clip, box), paddedRegion);
    if (!visible.width || !visible.height) continue;
    const videoWidth = video.videoWidth || video.width;
    const videoHeight = video.videoHeight || video.height;
    const sx = (mirrored ? box.left + box.width - visible.left - visible.width : visible.left - box.left) / box.width * videoWidth;
    const sy = (visible.top - box.top) / box.height * videoHeight;
    const sw = visible.width / box.width * videoWidth;
    const sh = visible.height / box.height * videoHeight;
    const x = (visible.left - region.left) * scaleX + padding;
    const y = (visible.top - region.top) * scaleY + padding;
    const w = visible.width * scaleX;
    const h = visible.height * scaleY;
    sampleCtx.save();
    try {
      if (mirrored) { sampleCtx.translate(x + w, y); sampleCtx.scale(-1, 1); }
      sampleCtx.drawImage(video, sx, sy, sw, sh, mirrored ? 0 : x, mirrored ? 0 : y, w, h);
      coverage.push(visible);
    } catch {
      // A stream can disappear between discovering it and drawing this frame.
    } finally { sampleCtx.restore(); }
  }

  // A full source ensures opaque pixels even when smaller videos overlap it.
  // Keep fallback for masks spanning independent video edges/transparent gaps.
  const covered = coverage.some(rect => coversRegion([rect], region));
  try {
    blurCells(sample, blurred, columns, rows, padding, radius, !coversRegion(coverage, paddedRegion));
  } catch {
    // A failed render uses the CSS blur rather than stale pixels.
    return false;
  }
  ctx.save();
  if (region.type === 'ellipse') {
    ctx.beginPath(); ctx.ellipse(outputWidth / 2, outputHeight / 2, outputWidth / 2, outputHeight / 2, 0, 0, Math.PI * 2); ctx.clip();
  }
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(blurred, 0, 0, columns, rows, 0, 0, outputWidth, outputHeight);
  ctx.restore();
  return covered;
}
